import { showConfirm } from '../components/ConfirmDialog';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfig, useConfigLoadError } from '../hooks/useConfig';
import { useWriteGate } from '../lib/writeGate';
import { useMusicians } from '../hooks/useMusicians';
import { useCrew } from '../hooks/useCrew';
import type { IemConfig, TransportType } from '../types';
import ConfigPageHeader from '../components/ConfigPageHeader';
import ConfigField from '../components/ConfigField';
import AutoTextarea from '../components/AutoTextarea';
import ProviderSelector from '../components/ProviderSelector';
import { useFocusField } from '../hooks/useFocusField';
import { selectedIemPeople } from '../lib/iemUnits';
import { useLayoutOrder } from '../hooks/useLayoutOrder';
import { sortByLayoutOrder } from '../lib/layoutOrder';
import PageLoadError from '../components/PageLoadError';

export default function ConfigIemPage() {
  const { config, updateIem, reload } = useConfig();
  // Lot 3a — the rider_config could not be read: the page would render EMPTY
  // fields as if the band had nothing typed. See PageLoadError.tsx.
  const configLoad = useConfigLoadError();

  // Capability-aware write gate (lot A2-CAP, 2026-09-25). Every write of this
  // page persists through `useConfig` -> PUT /api/bands/:id/rider/config, the
  // rider CONTENT: a globally `read-only` account keeps it ONLY through
  // `content.rider` (conditional pre-guard mount, server/index.js). NEVER the
  // blanket `canWrite` here — it would freeze the very crew member the
  // capability is for.
  const writeGate = useWriteGate();
  const canEditContent = writeGate.canWriteCap('content.rider');
  // The note describes the page's REAL state: it must NOT tell a globally
  // read-only account that editing is disabled while the capability that
  // unlocks THIS page is held (the fields are open). Show it only when the
  // page's own write predicate is denied.
  const readOnlyNote = writeGate.readOnlyReason === 'global-read-only' && !canEditContent;
  const { t } = useTranslation();
  useFocusField(true);
  // Reorder the IEM fields to mirror the Rider Layout's `iem.*` order.
  const layoutOrder = useLayoutOrder('iem');
  const [transport, setTransport] = useState<TransportType>('van');
  const { musicians } = useMusicians();
  const { crew } = useCrew();

  const cfg = config.iem[transport];

  const handleChange = (key: keyof IemConfig, value: string | number) => {
    updateIem({
      [transport]: { ...cfg, [key]: value },
    } as any);
  };

  const populateAll = async (src: TransportType) => {
    const srcCfg = config.iem[src];
    if (!(await showConfirm(t('configIem.populateAllConfirm', { src })))) return;
    updateIem({ [transport]: { ...srcCfg } } as any);
  };

  const populateField = (key: keyof IemConfig) => async (src: TransportType) => {
    const srcVal = config.iem[src][key];
    if (srcVal === cfg[key]) return;
    if (srcVal === undefined) return; // optional field absent in the source preset
    if (await showConfirm(`Overwrite "${key}" with the value from ${src}?`)) {
      handleChange(key, srcVal);
    }
  };

  // --- Bodypack summary (read-only) — mirrors the rider PDF's IEM section ---
  // The transmitter/bodypack count is derived from the SELECTED musicians/crew
  // in-ear choice (iemType != 'none'), the same way buildPdfDoc computes it.
  // Each row lists a selected person and their IEM choice.
  const IEM_LABELS: Record<string, string> = { none: 'configIem.noInEars', stereo: 'configIem.stereo', mono: 'configIem.mono' };
  // Same shared helper as the rider PDF (src/lib/iemUnits.ts) — the list below
  // IS the count, so the two can never drift again.
  const iemSummaryRows = selectedIemPeople(
    musicians, crew, config.rider?.activeMusicianIds, config.rider?.activeCrewIds,
  ).map((p) => ({
    name: p.name || p.role || t('configIem.unnamed'),
    iemType: p.iemType || 'none',
    color: p.color ?? undefined,
    wired: p.iemWired === true,
  }));
  // Wired in-ears are fed from the monitor rack → NO transmitter to provide, so
  // they are listed but excluded from the transmitter count (same rule as the
  // rider's "Units needed" line — shared helper).
  // Le récap est scindé en deux listes : les émetteurs à fournir (sans fil, ce
  // qui est compté) et les in-ears FILAIRES, listés POUR INFO (aucun émetteur).
  const wirelessRows = iemSummaryRows.filter((r) => !r.wired);
  const wiredRows = iemSummaryRows.filter((r) => r.wired);
  // WEDGES (retours) + SUBWOOFER : totaux dérivés des personnes SÉLECTIONNÉES
  // (cartes musician/crew — mêmes champs que le rider PDF), 2026-10-17.
  const selectedAll = [
    ...(musicians || []).filter((m) => config.rider?.activeMusicianIds?.includes(m.id)),
    ...(crew || []).filter((c) => config.rider?.activeCrewIds?.includes(c.id)),
  ];
  const wedgesTotal = selectedAll.reduce((sum, m) => sum + (m.wedges || 0), 0);
  const subCount = selectedAll.filter((m) => m.subwoofer).length;
  const transmitters = wirelessRows.length;
  const wiredCount = wiredRows.length;
  const bodypackTotal = transmitters + (cfg.extraBodypacks || 0);

  // Each Rider Layout field (`iem.*`) is its own reorderable block, so
  // reordering in the Layout Editor is reflected here. Blocks whose id is not
  // in the layout keep their default relative order (via sortByLayoutOrder).
  const blocks = [
    {
      id: 'iem.system',
      render: () => (
        <div className="form-section" data-field-id="iem.system">
          <h2>{t('configIem.system')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configIem.systemLabel')} current={transport} fieldId="iem.system" exampleText="Sennheiser G3/G4, Shure PSM1000" onUseExample={(t) => handleChange('system', t)} onPopulateFrom={populateField('system')}>
            <AutoTextarea disabled={!canEditContent}
              value={cfg.system}
              onChange={(e) => handleChange('system', e.target.value)}
              placeholder="e.g. Sennheiser G3/G4, Shure PSM1000"
            />
          </ConfigField>
          <ProviderSelector disabled={!canEditContent} value={cfg.provider || 'venue'} onChange={(v) => handleChange('provider', v)} />
        </div>
      ),
    },
    {
      id: 'iem.frequency',
      render: () => (
        <div className="form-section" data-field-id="iem.frequency">
          <h2>{t('configIem.frequencyRange')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configIem.frequencyRange')} current={transport} fieldId="iem.frequency" exampleText="470-565 MHz (G-band)" onUseExample={(t) => handleChange('frequencyRange', t)} onPopulateFrom={populateField('frequencyRange')}>
            <AutoTextarea disabled={!canEditContent}
              value={cfg.frequencyRange || ''}
              onChange={(e) => handleChange('frequencyRange', e.target.value)}
              placeholder="e.g. 470-565 MHz (G-band)"
            />
          </ConfigField>
        </div>
      ),
    },
    {
      id: 'iem.bodypacks',
      render: () => (
        <div className="form-section" data-field-id="iem.bodypacks">
          <h2>{t('configIem.bodypacks')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configIem.bodypacksLabel')} current={transport} fieldId="iem.bodypacks" onPopulateFrom={populateField('extraBodypacks')}>
            <input disabled={!canEditContent}
              type="number"
              value={cfg.extraBodypacks || 0}
              onChange={(e) => {
                const extra = parseInt(e.target.value, 10) || 0;
                updateIem({ [transport]: { ...cfg, extraBodypacks: extra } } as any);
              }}
              min="0"
            />
          </ConfigField>
          <p style={{ margin: '-6px 0 10px', color: 'var(--text-dim)', fontSize: '0.8rem' }}>
            {t('configIem.bodypacksHint')}
          </p>

          {/* Bodypack summary (read-only) — mirrors the rider PDF IEM section */}
          {iemSummaryRows.length > 0 && (
            <div style={{ marginTop: 4, marginBottom: 18 }}>
              <div className="form-group" style={{ marginBottom: 10 }}>
                <label>{t('configIem.total')}</label>
                <div className="form-input" style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', height: 40, padding: '0 12px' }}>
                  <span>{t('configIem.transmitters', { count: transmitters })}</span>
                  <span>{t('configIem.bodypacksTotal', { count: bodypackTotal })}</span>
                  {wiredCount > 0 && <span>{t('configIem.wiredCount', { count: wiredCount })}</span>}
                  <span style={{ color: 'var(--text-dim)' }}>{t('configIem.txPlusExtra')}</span>
                </div>
              </div>

              <label style={{ fontSize: '0.8rem', color: 'var(--text-dim)', display: 'block', marginBottom: 6 }}>
                {t('configIem.transmittersSelected')}
              </label>
              <div className="backline-list">
                {wirelessRows.map((r, i) => (
                  <div key={i} className="backline-row" style={{ gridTemplateColumns: '24px minmax(0,1fr) 140px' }}>
                    <span title={r.color} style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: r.color || 'var(--text-dim)', border: '1px solid var(--border)', margin: '0 auto' }} />
                    <span className="backline-label" style={{ display: 'inline-flex', alignItems: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', minHeight: 34, padding: '4px 8px' }}>{r.name}</span>
                    <span className="backline-type" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', height: 34, padding: '0 8px' }}>{t(IEM_LABELS[r.iemType] || r.iemType)}</span>
                  </div>
                ))}
              </div>
              {wiredRows.length > 0 && (
                <>
                  <label style={{ fontSize: '0.8rem', color: 'var(--text-dim)', display: 'block', margin: '12px 0 6px' }}>
                    {t('configIem.wiredSelected')}
                  </label>
                  <div className="backline-list">
                    {wiredRows.map((r, i) => (
                      <div key={i} className="backline-row" style={{ gridTemplateColumns: '24px minmax(0,1fr) 140px' }}>
                        <span title={r.color} style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: r.color || 'var(--text-dim)', border: '1px solid var(--border)', margin: '0 auto' }} />
                        <span className="backline-label" style={{ display: 'inline-flex', alignItems: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', minHeight: 34, padding: '4px 8px' }}>{r.name}</span>
                        <span className="backline-type" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', height: 34, padding: '0 8px' }}>{t(IEM_LABELS[r.iemType] || r.iemType)}<span style={{ color: 'var(--text-dim)' }}>· {t('configIem.wired')}</span></span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* WEDGES (retours) + SUBWOOFER moved to their OWN block (2026-10-18,
              user): the Note field now sits right below BODYPACKS. */}
        </div>
      ),
    },
    {
      id: 'iem.note',
      render: () => (
        <div className="form-section" data-field-id="iem.note">
          <h2>{t('configIem.note')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configIem.noteLabel')} current={transport} fieldId="iem.note" exampleText="Avoid frequencies 600-700 MHz, hard-wired packs preferred, spare batteries…" onUseExample={(t) => handleChange('notes', t)} onPopulateFrom={populateField('notes')} labelBlock>
            <AutoTextarea disabled={!canEditContent}
              value={cfg.notes || ''}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="e.g. Avoid frequencies 600-700 MHz, hard-wired packs preferred, spare batteries…"
            />
          </ConfigField>
        </div>
      ),
    },
    {
      id: 'iem.wedges',
      render: () => (
        <div className="form-section" data-field-id="iem.wedges">
          <h2>{t('configIem.wedgesSub')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configIem.wedgesProvider')} current={transport} fieldId="iem.wedges">
            <ProviderSelector disabled={!canEditContent} value={cfg.wedgesProvider || 'venue'} onChange={(v) => handleChange('wedgesProvider', v)} />
          </ConfigField>
          <ConfigField disabled={!canEditContent} label={t('configIem.subProvider')} current={transport} fieldId="iem.sub">
            <ProviderSelector disabled={!canEditContent} value={cfg.subProvider || 'venue'} onChange={(v) => handleChange('subProvider', v)} />
          </ConfigField>
          <p style={{ margin: '-6px 0 0', color: 'var(--text-dim)', fontSize: '0.8rem' }}>
            {t('configIem.wedgesHint', { wedges: wedgesTotal, sub: subCount })}
          </p>
        </div>
      ),
    },
  ];

  const sortedBlocks = sortByLayoutOrder(blocks, layoutOrder, (b) => b.id);


  // Lot 3a: the rider_config could not be read → empty fields would read as
  // "this band typed nothing". Show the shared state instead; the writes stay
  // gated by `useConfig` skipSave, so a retry cannot wipe anything.
  if (configLoad.failed) {
    return (
      <PageLoadError
        title={t('configIem.title')}
        detail={configLoad.message}
        onRetry={() => void reload().catch(() => undefined)}
      />
    );
  }

  return (
    <div className="page config-page" data-config-page="iem" data-density="socle">
      <ConfigPageHeader disabled={!canEditContent}
        iconKey="configIem.title"
        title={t('configIem.title')}
        transport={transport}
        onTransportChange={setTransport}
        onPopulateAll={populateAll}
      />

      {readOnlyNote && (
        <p className="text-dim" data-readonly-note style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 12px' }}>{t('common.readOnlyAccount')}</p>
      )}
      <div className="edit-form">
        {sortedBlocks.map((b) => b.render())}
      </div>
    </div>
  );
}
